import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  Globe2,
  Loader2,
  Mail,
  Mountain,
  Orbit,
  Plane,
  User,
  Waves,
} from 'lucide-react';
import { api } from '../context/AuthContext';
import ConstellationBackdrop from '../components/ConstellationBackdrop';
import ArclipseBrandLockup from '../components/ArclipseBrandLockup';
import './ConstellationIntake.css';

const DOMAIN_OPTIONS = [
  { value: 'space', label: 'Space', icon: Orbit },
  { value: 'air', label: 'Air', icon: Plane },
  { value: 'sea', label: 'Sea', icon: Waves },
  { value: 'land', label: 'Land', icon: Mountain },
];

const STAGES = [
  'Idea / research',
  'Pre-seed',
  'Seed',
  'Series A',
  'Bootstrapped / revenue funded',
  'Other',
];

const APPLICATION_STEPS = [
  { number: '01', title: 'Founder', label: 'Founder and company' },
  { number: '02', title: 'Company', label: 'Company signal' },
  { number: '03', title: 'Fit', label: 'Constellation fit' },
];

const LEGAL_VERSION = '2026-06-10';

const EMPTY_APPLICATION = {
  founder_name: '',
  email: '',
  company_name: '',
  country: '',
  website: '',
  linkedin: '',
  deck_url: '',
  domains: [],
  stage: '',
  company_summary: '',
  technical_edge: '',
  evidence: '',
  constellation_need: '',
  consent: false,
  terms_version: LEGAL_VERSION,
  privacy_version: LEGAL_VERSION,
};

function IntakeNav() {
  return (
    <header className="intake-nav">
      <Link to="/" className="intake-brand" aria-label="Back to ARCLIPSE Constellation">
        <ArclipseBrandLockup size="sm" />
      </Link>
      <Link to="/" className="intake-back">
        <ArrowLeft size={14} />
        <span>Back</span>
      </Link>
    </header>
  );
}

function Field({ label, hint, error, children, wide = false }) {
  return (
    <div className={`intake-field ${wide ? 'intake-field--wide' : ''}`}>
      <div className="intake-field-head">
        <label>{label}</label>
        {hint && <span>{hint}</span>}
      </div>
      {children}
      {error && <div className="intake-error">{error}</div>}
    </div>
  );
}

function TextInput({ icon: Icon, ...props }) {
  return (
    <div className="intake-input-wrap">
      {Icon && <Icon size={15} aria-hidden="true" />}
      <input {...props} />
    </div>
  );
}

function SectionHead({ number, title, copy }) {
  return (
    <div className="intake-section-head">
      <span>{number}</span>
      <div>
        <h2>{title}</h2>
        <p>{copy}</p>
      </div>
    </div>
  );
}

function validateApplication(form) {
  const errors = {};
  if (form.founder_name.trim().length < 2) errors.founder_name = 'Enter your name.';
  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errors.email = 'Enter a valid email.';
  if (form.company_name.trim().length < 2) errors.company_name = 'Enter the company name.';
  if (form.country.trim().length < 2) errors.country = 'Enter your country.';
  if (!form.domains.length) errors.domains = 'Choose at least one domain.';
  if (!form.stage) errors.stage = 'Choose the closest stage.';
  if (form.company_summary.trim().length < 20) errors.company_summary = 'Give us at least one clear sentence.';
  if (form.technical_edge.trim().length < 30) errors.technical_edge = 'Tell us what is technically difficult or different.';
  if (form.evidence.trim().length < 10) errors.evidence = 'Share the strongest evidence you have today.';
  if (form.constellation_need.trim().length < 10) errors.constellation_need = 'Tell us what you believe is materially required.';
  if (!form.consent) errors.consent = 'Accept the Terms and acknowledge the Privacy Policy.';
  return errors;
}

function errorsForStep(form, step) {
  const allErrors = validateApplication(form);
  const fields = [
    ['founder_name', 'email', 'company_name', 'country'],
    ['domains', 'stage', 'company_summary', 'technical_edge', 'evidence'],
    ['constellation_need', 'consent'],
  ][step];
  return Object.fromEntries(fields.filter(field => allErrors[field]).map(field => [field, allErrors[field]]));
}

export function FounderApplication() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_APPLICATION);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const completion = useMemo(() => {
    const checks = [
      form.founder_name.trim().length >= 2,
      /^\S+@\S+\.\S+$/.test(form.email.trim()),
      form.company_name.trim().length >= 2,
      form.country.trim().length >= 2,
      form.domains.length > 0,
      Boolean(form.stage),
      form.company_summary.trim().length >= 20,
      form.technical_edge.trim().length >= 30,
      form.evidence.trim().length >= 10,
      form.constellation_need.trim().length >= 10,
      form.consent,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [form]);

  const setValue = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
    setSubmitError('');
  };

  const toggleDomain = (domain) => {
    const next = form.domains.includes(domain)
      ? form.domains.filter(item => item !== domain)
      : [...form.domains, domain];
    setValue('domains', next);
  };

  const continueApplication = () => {
    const nextErrors = errorsForStep(form, currentStep);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    setCurrentStep(step => Math.min(step + 1, APPLICATION_STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const returnStep = () => {
    setErrors({});
    setCurrentStep(step => Math.max(0, step - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async (event) => {
    event.preventDefault();
    const nextErrors = validateApplication(form);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      document.querySelector('.intake-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setBusy(true);
    setSubmitError('');
    try {
      const response = await api.post('/constellation/apply', form);
      navigate('/application-received', {
        state: {
          kind: 'application',
          founderName: form.founder_name.trim(),
          companyName: form.company_name.trim(),
          email: form.email.trim(),
          reference: response.data.application_id,
          already: response.data.already,
        },
      });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setSubmitError(typeof detail === 'string' ? detail : 'We could not submit the application. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="intake-root">
      <ConstellationBackdrop />
      <IntakeNav />

      <main className="intake-layout">
        <aside className="intake-rail">
          <div className="intake-status"><span /> Cohort 1 open</div>
          <h1>Show us what you are building.</h1>
          <p>
            A short founder application for frontier companies across space, air, sea, and land.
            Every submission is reviewed by hand.
          </p>

          <div className="intake-step-nav" aria-label="Application steps">
            {APPLICATION_STEPS.map((step, index) => (
              <button
                type="button"
                key={step.number}
                className={`${index === currentStep ? 'is-current' : ''} ${index < currentStep ? 'is-complete' : ''}`}
                onClick={() => index < currentStep && setCurrentStep(index)}
                disabled={index > currentStep}
              >
                <span>{index < currentStep ? <Check size={13} /> : step.number}</span>
                <div><strong>{step.title}</strong><small>{step.label}</small></div>
              </button>
            ))}
          </div>

          <div className="intake-progress" aria-label={`${completion}% complete`}>
            <div className="intake-progress-head">
              <span>Complete</span>
              <strong>{completion}%</strong>
            </div>
            <div className="intake-progress-track"><span style={{ width: `${completion}%` }} /></div>
          </div>

          <p className="intake-rail-link">
            Not ready to apply? <Link to="/join">Join the waitlist</Link>.
          </p>
        </aside>

        <form className="intake-window" onSubmit={submit} noValidate>
          <div className="intake-window-top">
            <div>
              <span>{APPLICATION_STEPS[currentStep].label}</span>
              <strong>Step {currentStep + 1} of {APPLICATION_STEPS.length}</strong>
            </div>
            <span className="intake-live-node" aria-hidden="true" />
          </div>

          {currentStep === 0 && <section>
            <SectionHead number="01" title="Founder and company" copy="Enough context to know who is applying and where the work lives." />
            <div className="intake-grid">
              <Field label="Your name" error={errors.founder_name}>
                <TextInput icon={User} value={form.founder_name} onChange={e => setValue('founder_name', e.target.value)} autoComplete="name" />
              </Field>
              <Field label="Email" error={errors.email}>
                <TextInput icon={Mail} type="email" value={form.email} onChange={e => setValue('email', e.target.value)} autoComplete="email" />
              </Field>
              <Field label="Company name" error={errors.company_name}>
                <TextInput icon={Building2} value={form.company_name} onChange={e => setValue('company_name', e.target.value)} autoComplete="organization" />
              </Field>
              <Field label="Country" error={errors.country}>
                <TextInput icon={Globe2} value={form.country} onChange={e => setValue('country', e.target.value)} autoComplete="country-name" />
              </Field>
              <Field label="Website" hint="Optional">
                <TextInput value={form.website} onChange={e => setValue('website', e.target.value)} placeholder="https://" inputMode="url" />
              </Field>
              <Field label="LinkedIn" hint="Optional">
                <TextInput value={form.linkedin} onChange={e => setValue('linkedin', e.target.value)} placeholder="Founder or company profile" inputMode="url" />
              </Field>
            </div>
          </section>}

          {currentStep === 1 && <section>
            <SectionHead number="02" title="The company" copy="Keep it direct. We care about the system, the difficulty and the evidence." />
            <Field label="Domain" error={errors.domains} wide>
              <div className="intake-domain-grid">
                {DOMAIN_OPTIONS.map(({ value, label, icon: Icon }) => {
                  const selected = form.domains.includes(value);
                  return (
                    <button
                      type="button"
                      key={value}
                      className={selected ? 'is-selected' : ''}
                      onClick={() => toggleDomain(value)}
                      aria-pressed={selected}
                    >
                      <Icon size={17} />
                      <span>{label}</span>
                      {selected && <Check size={14} />}
                    </button>
                  );
                })}
              </div>
            </Field>

            <div className="intake-grid">
              <Field label="Current stage" error={errors.stage} wide>
                <div className="intake-select-wrap">
                  <select value={form.stage} onChange={e => setValue('stage', e.target.value)}>
                    <option value="">Select stage</option>
                    {STAGES.map(stage => <option key={stage} value={stage}>{stage}</option>)}
                  </select>
                </div>
              </Field>
              <Field label="Describe the company in one sentence" hint={`${form.company_summary.length}/500`} error={errors.company_summary} wide>
                <textarea rows={3} maxLength={500} value={form.company_summary} onChange={e => setValue('company_summary', e.target.value)} placeholder="We build..." />
              </Field>
              <Field label="What is technically difficult or different?" hint={`${form.technical_edge.length}/1400`} error={errors.technical_edge} wide>
                <textarea rows={5} maxLength={1400} value={form.technical_edge} onChange={e => setValue('technical_edge', e.target.value)} placeholder="Explain the underlying system, technical edge, IP, research or hard constraint." />
              </Field>
              <Field label="What evidence exists today?" hint={`${form.evidence.length}/1200`} error={errors.evidence} wide>
                <textarea rows={4} maxLength={1200} value={form.evidence} onChange={e => setValue('evidence', e.target.value)} placeholder="Prototype, tests, users, revenue, pilots, research, partnerships or other proof." />
              </Field>
            </div>
          </section>}

          {currentStep === 2 && <section>
            <SectionHead number="03" title="Why Constellation" copy="Tell us what needs to change for this company to move faster." />
            <div className="intake-grid">
              <Field label="What do you believe is materially required right now?" hint={`${form.constellation_need.length}/1000`} error={errors.constellation_need} wide>
                <textarea rows={4} maxLength={1000} value={form.constellation_need} onChange={e => setValue('constellation_need', e.target.value)} placeholder="Your view on the capital, access, talent, validation or technical support required to move the company forward." />
              </Field>
              <Field label="Pitch deck or data room" hint="Optional" wide>
                <TextInput value={form.deck_url} onChange={e => setValue('deck_url', e.target.value)} placeholder="Private link is fine" inputMode="url" />
              </Field>
            </div>

            <label className={`intake-consent ${errors.consent ? 'has-error' : ''}`}>
              <input type="checkbox" checked={form.consent} onChange={e => setValue('consent', e.target.checked)} />
              <span className="intake-check"><Check size={12} /></span>
              <span>
                I confirm this information is accurate, agree to the <Link to="/terms">Terms of Use</Link>,
                and acknowledge the <Link to="/privacy">Privacy Policy</Link>.
              </span>
            </label>
            {errors.consent && <div className="intake-error intake-consent-error">{errors.consent}</div>}
          </section>}

          {submitError && <div className="intake-submit-error">{submitError}</div>}

          <div className="intake-submit-row">
            <div>
              <strong>{currentStep === 2 ? 'Ready for review' : 'Your answers stay on this page'}</strong>
              <span>{currentStep === 2 ? 'No ARC assessment is required now.' : 'Continue when this section is complete.'}</span>
            </div>
            <div className="intake-submit-actions">
              {currentStep > 0 && <button type="button" className="intake-secondary-action" onClick={returnStep}>Back</button>}
              {currentStep < APPLICATION_STEPS.length - 1 ? (
                <button type="button" onClick={continueApplication}>Continue <ArrowRight size={16} /></button>
              ) : (
                <button type="submit" disabled={busy}>
                  {busy ? <><Loader2 size={16} className="intake-spin" /> Submitting</> : <>Submit application <ArrowRight size={16} /></>}
                </button>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}

export function ConstellationWaitlist() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    founder_name: '',
    email: '',
    company_name: '',
    domain: '',
    consent: false,
    terms_version: LEGAL_VERSION,
    privacy_version: LEGAL_VERSION,
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (form.founder_name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(form.email.trim()) || !form.consent) {
      setError(!form.consent ? 'Please accept the Terms and acknowledge the Privacy Policy.' : 'Add your name and a valid email.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await api.post('/constellation/waitlist', form);
      navigate('/application-received', {
        state: {
          kind: 'waitlist',
          founderName: form.founder_name.trim(),
          companyName: form.company_name.trim(),
          email: form.email.trim(),
          reference: response.data.waitlist_id,
          already: response.data.already,
        },
      });
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'We could not save your place. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="intake-root intake-root--compact">
      <ConstellationBackdrop />
      <IntakeNav />
      <main className="intake-compact-layout">
        <div className="intake-compact-copy">
          <div className="intake-status"><span /> Constellation waitlist</div>
          <h1>Stay close to the signal.</h1>
          <p>
            Not ready for a full application? Join the waitlist and we will let you know when the next founder intake opens.
          </p>
          <Link to="/apply">Ready now? Apply instead <ArrowRight size={14} /></Link>
        </div>

        <form className="intake-window intake-window--compact" onSubmit={submit} noValidate>
          <div className="intake-window-top">
            <div><span>Founder waitlist</span><strong>Under one minute</strong></div>
            <Mail size={18} />
          </div>
          <div className="intake-grid intake-grid--single">
            <Field label="Your name">
              <TextInput icon={User} value={form.founder_name} onChange={e => setForm(prev => ({ ...prev, founder_name: e.target.value }))} autoComplete="name" />
            </Field>
            <Field label="Email">
              <TextInput icon={Mail} type="email" value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} autoComplete="email" />
            </Field>
            <Field label="Company" hint="Optional">
              <TextInput icon={Building2} value={form.company_name} onChange={e => setForm(prev => ({ ...prev, company_name: e.target.value }))} />
            </Field>
            <Field label="Closest domain" hint="Optional">
              <div className="intake-select-wrap">
                <select value={form.domain} onChange={e => setForm(prev => ({ ...prev, domain: e.target.value }))}>
                  <option value="">Choose a domain</option>
                  {DOMAIN_OPTIONS.map(domain => <option key={domain.value} value={domain.value}>{domain.label}</option>)}
                </select>
              </div>
            </Field>
          </div>
          <label className="intake-consent intake-consent--compact">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={e => setForm(prev => ({ ...prev, consent: e.target.checked }))}
            />
            <span className="intake-check"><Check size={12} /></span>
            <span>
              I agree to the <Link to="/terms">Terms</Link> and acknowledge the <Link to="/privacy">Privacy Policy</Link>.
            </span>
          </label>
          {error && <div className="intake-submit-error">{error}</div>}
          <button className="intake-compact-submit" type="submit" disabled={busy}>
            {busy ? <><Loader2 size={16} className="intake-spin" /> Saving</> : <>Join the waitlist <ArrowRight size={16} /></>}
          </button>
          <p className="intake-privacy">No assessment. No pitch deck. Your details are used to administer and contact you about Constellation.</p>
        </form>
      </main>
    </div>
  );
}

export function IntakeReceived() {
  const location = useLocation();
  const state = location.state || {};
  const isApplication = state.kind === 'application';

  return (
    <div className="intake-root intake-root--compact">
      <ConstellationBackdrop variant="success" />
      <IntakeNav />
      <main className="intake-received">
        <div className="intake-received-node"><Check size={26} /></div>
        <div className="intake-status"><span /> {state.already ? 'Details updated' : 'Signal received'}</div>
        <h1>{isApplication ? 'Your application is in.' : "You're on the waitlist."}</h1>
        <p>
          {isApplication
            ? `${state.companyName || 'Your company'} is now in the Constellation review queue. We will contact ${state.email || 'you'} if there is a strong next step.`
            : `We saved your place${state.email ? ` under ${state.email}` : ''}. We will reach out when the next relevant intake opens.`}
        </p>

        {state.reference && (
          <div className="intake-reference">
            <span>Reference</span>
            <strong>{state.reference}</strong>
          </div>
        )}

        {isApplication && (
          <div className="intake-received-steps">
            <div><span>01</span><p><strong>Manual review</strong>Your company and evidence are read first.</p></div>
            <div><span>02</span><p><strong>ARC invitation</strong>Used only when deeper founder context is useful.</p></div>
            <div><span>03</span><p><strong>Founder call</strong>A direct conversation when there is genuine fit.</p></div>
          </div>
        )}

        <div className="intake-received-actions">
          <Link to="/" className="intake-primary-link">Return to Constellation <ArrowRight size={15} /></Link>
          {!isApplication && <Link to="/apply" className="intake-text-link">Complete an application</Link>}
        </div>
      </main>
    </div>
  );
}
